import { makeAutoObservable, runInAction } from 'mobx';

import { snippetsService } from '@/services/snippets';
import type { UserSnippet } from '@/types/referral';

class SnippetsStore {
  snippets: UserSnippet[] = [];

  loaded = false;
  refreshing = false;
  refreshError: string | null = null;

  creating = false;
  createError: string | null = null;

  removing = false;
  removeError: string | null = null;

  /** Tracks an in-flight `ensureLoaded` so concurrent callers share it. */
  private inFlightEnsure: Promise<void> | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  /**
   * Lazy load. Concurrent callers share a single in-flight refresh —
   * each `await ensureLoaded()` resolves only after the load completes,
   * never beforehand.
   */
  ensureLoaded(): Promise<void> {
    if (this.loaded) return Promise.resolve();
    if (this.inFlightEnsure) return this.inFlightEnsure;
    this.inFlightEnsure = this.refresh()
      .catch(() => {
        // Error captured on store.refreshError.
      })
      .finally(() => {
        runInAction(() => {
          this.inFlightEnsure = null;
        });
      });
    return this.inFlightEnsure;
  }

  async refresh(): Promise<void> {
    runInAction(() => {
      this.refreshing = true;
      this.refreshError = null;
    });
    try {
      const snippets = await snippetsService.list();
      runInAction(() => {
        this.snippets = snippets;
        this.loaded = true;
      });
    } catch (err) {
      runInAction(() => {
        this.refreshError = err instanceof Error ? err.message : 'Failed to load snippets.';
      });
      throw err;
    } finally {
      runInAction(() => {
        this.refreshing = false;
      });
    }
  }

  async create(body: string): Promise<UserSnippet> {
    runInAction(() => {
      this.creating = true;
      this.createError = null;
    });
    try {
      const created = await snippetsService.create({ body });
      runInAction(() => {
        // Prepend so newest is first; matches server's recent_first ordering.
        this.snippets = [created, ...this.snippets];
        this.creating = false;
      });
      return created;
    } catch (err) {
      runInAction(() => {
        this.createError = err instanceof Error ? err.message : 'Failed to save snippet.';
        this.creating = false;
      });
      throw err;
    }
  }

  async remove(id: number): Promise<void> {
    runInAction(() => {
      this.removing = true;
      this.removeError = null;
    });
    try {
      await snippetsService.remove(id);
      runInAction(() => {
        this.snippets = this.snippets.filter((s) => s.id !== id);
        this.removing = false;
      });
    } catch (err) {
      runInAction(() => {
        this.removeError = err instanceof Error ? err.message : 'Failed to delete snippet.';
        this.removing = false;
      });
      throw err;
    }
  }

  reset(): void {
    runInAction(() => {
      this.snippets = [];
      this.loaded = false;
      this.refreshing = false;
      this.refreshError = null;
      this.creating = false;
      this.createError = null;
      this.removing = false;
      this.removeError = null;
    });
  }
}

export const snippetsStore = new SnippetsStore();
export { SnippetsStore };
